import { type Ref, type TransitionEventHandler, useCallback, useImperativeHandle, useState } from "react";
import { CollapsedNavigationHoverZone } from "./CollapsedNavigationHoverZone";
import { LeftNavigationPanel } from "./LeftNavigationPanel";
import { LeftNavigationResizer } from "./LeftNavigationResizer";
import { LeftNavigationSlot } from "./LeftNavigationSlot";
import { useLeftNavigationWidthPreview } from "./useLeftNavigationWidthPreview";
import { useNavigationHoverTracking } from "./useNavigationHoverTracking";

interface LeftNavigationLayoutProps {
	leftNavigationContent: JSX.Element;
	leftNavigationTop?: JSX.Element;
	leftNavigationBottom?: JSX.Element;
	isVisible: boolean;
	isCollapsed: boolean;
	isMobile?: boolean;
	onTransitionEnd?: TransitionEventHandler<HTMLDivElement>;
	onResizingChange?: (isResizing: boolean) => void;
	sidebarRef?: Ref<HTMLDivElement>;
}

const LeftNavigationLayout = ({
	leftNavigationContent,
	leftNavigationTop,
	leftNavigationBottom,
	isVisible,
	isCollapsed,
	isMobile = false,
	onTransitionEnd,
	onResizingChange,
	sidebarRef,
}: LeftNavigationLayoutProps) => {
	const [isResizing, setIsResizing] = useState(false);
	const { navigationRef, bottomRef, previewWidth } = useLeftNavigationWidthPreview();
	const { trackHoveredItem, hoverSidebar, clearHover } = useNavigationHoverTracking(isResizing);
	useImperativeHandle(sidebarRef, () => navigationRef.current);

	const handleResizingChange = useCallback(
		(nextIsResizing: boolean) => {
			setIsResizing(nextIsResizing);
			onResizingChange?.(nextIsResizing);
		},
		[onResizingChange],
	);

	return (
		<>
			{isCollapsed && <CollapsedNavigationHoverZone />}
			<LeftNavigationPanel
				bottom={leftNavigationBottom}
				content={leftNavigationContent}
				isCollapsed={isCollapsed}
				isMobile={isMobile}
				isResizing={isResizing}
				isVisible={isVisible}
				onPointerLeave={clearHover}
				onPointerOver={trackHoveredItem}
				onTransitionEnd={onTransitionEnd}
				ref={navigationRef}
				top={leftNavigationTop}
			/>
			{!isMobile && (
				<LeftNavigationResizer
					hasBottom={!!leftNavigationBottom}
					isCollapsed={isCollapsed}
					isVisible={isVisible}
					onPointerEnter={hoverSidebar}
					onPointerLeave={clearHover}
					onResize={previewWidth}
					onResizingChange={handleResizingChange}
				/>
			)}
			{!isMobile && leftNavigationBottom && (
				<LeftNavigationSlot containerRef={bottomRef}>{leftNavigationBottom}</LeftNavigationSlot>
			)}
		</>
	);
};

export default LeftNavigationLayout;
